import { useEffect } from 'react';
import { SKIPSCREEN_DASHBOARD_REFRESH_EVENT } from '../utils/realtimeEvents';

const VISIBILITY_DEBOUNCE_MS = 400;
const visibilityListeners = new Set<() => void>();
let visibilityRefreshTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityListenerAttached = false;

function flushVisibilityRefresh() {
  visibilityRefreshTimer = null;
  visibilityListeners.forEach((listener) => listener());
}

function scheduleVisibilityRefresh() {
  if (visibilityRefreshTimer) {
    clearTimeout(visibilityRefreshTimer);
  }
  visibilityRefreshTimer = setTimeout(flushVisibilityRefresh, VISIBILITY_DEBOUNCE_MS);
}

function ensureVisibilityListener() {
  if (visibilityListenerAttached || typeof document === 'undefined') {
    return;
  }
  visibilityListenerAttached = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleVisibilityRefresh();
    }
  });
}

/**
 * Refetch dashboard data when socket events fire or the tab becomes visible again.
 * Visibility refreshes are debounced and shared across all subscribers.
 */
export function useDashboardRealtimeRefresh(onRefresh: () => void): void {
  useEffect(() => {
    ensureVisibilityListener();
    visibilityListeners.add(onRefresh);
    window.addEventListener(SKIPSCREEN_DASHBOARD_REFRESH_EVENT, onRefresh);

    return () => {
      visibilityListeners.delete(onRefresh);
      window.removeEventListener(SKIPSCREEN_DASHBOARD_REFRESH_EVENT, onRefresh);
    };
  }, [onRefresh]);
}
